import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import AttachmentsDropzone from '../controls/AttachmentsDropzone';
import CurrencyField from '../fields/CurrencyField';
import DateField from '../fields/DateField';
import NavRow from '../fields/NavRow';
import TextField from '../fields/TextField';
import { TaskDefinition, TaskStepRenderArgs } from '../QuestionnaireShell';
import { InsuranceValues } from '../types';
import { currencyAmountSchema, zodResolver } from '../validation';

const schema = z.object({
  attachments: z.array(z.any()).min(1, 'Please upload at least one file'),
  policy_number: z
    .string({ required_error: 'Policy Number is required but is missing' })
    .min(1, 'Policy Number is required but is missing')
    .max(20),
  policy_carrier: z
    .string({ required_error: 'Carrier is required but is missing' })
    .min(1, 'Carrier is required but is missing'),
  policy_amount: currencyAmountSchema,
  policy_expiration: z
    .string({ required_error: 'Expiration Date is required but is missing' })
    .min(1, 'Expiration Date is required but is missing'),
});

const DEFAULTS: InsuranceValues = {
  attachments: [],
  policy_number: '',
  policy_carrier: '',
  policy_amount: { display: '', value: 0 },
  policy_expiration: '',
};

const InsuranceForm = ({ initialValues, onComplete, onBack, onCancelEdit, submitLabel }: TaskStepRenderArgs) => {
  const defaults = (initialValues as InsuranceValues | undefined) ?? DEFAULTS;
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<InsuranceValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit((values) => onComplete(values));

  return (
    <form className="rnq-form" onSubmit={(e) => e.preventDefault()}>
      <AttachmentsDropzone control={control} name="attachments" required />
      <TextField control={control} name="policy_number" label="Policy Number" required maxLength={20} />
      <TextField control={control} name="policy_carrier" label="Carrier" required />
      <CurrencyField control={control} name="policy_amount" label="Amount (USD)" required />
      <DateField control={control} name="policy_expiration" label="Expiration" required disablePast />
      <NavRow
        onBack={onBack}
        onCancelEdit={onCancelEdit}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!isValid}
      />
    </form>
  );
};

const Insurance: TaskDefinition = {
  key: 'task14',
  label: 'Insurance',
  title: 'Insurance Details',
  description: 'Please upload a copy of your insurance declaration.',
  render: (args) => <InsuranceForm {...args} />,
};

export default Insurance;
